// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2016
//
// Include description of object & useful info here, please!
import { romanToInt, validRomans } from 'shared/definitions/musicTheory/romanNumerals';
import type { AnswerAndData } from 'shared/definitions/answer-and-data';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import * as Skills from '../uTheory.Skills/skills';
import type { RomanNumeralQuestionOptions } from '../uTheory.Skills/romanNumeralQuestionGenerator';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import KeySignature from '../uTheory.Notation/keySignature';
import RomanNumeral from '../uTheory.Notation/romanNumeral';
import utils from '../utils';
import Chord from '../uTheory.Notation/chord';

export type RomanQualityLabelQuestion = {
  rn: RomanNumeral;
  sd: RomanNumeral['scaleDegree'];
  isSeventh: RomanNumeral['isSeventh'];
  isMajorKey: boolean;
  quality: RomanNumeral['chord']['quality'];
  skills: string[];
  text: string[];
  questionText: string;
};

interface RomanQualityLabelOptions extends InteractiveOptions {
  /** Only needed when in manual questions mode. */
  chordTypes: RomanNumeralQuestionOptions['chordTypes'];
  questions?: string[];
}

/**
 * What's the quality of a triad/seventh chord on scale degree 2?
 */
class RomanQualityLabel extends InteractivePrototype {
  declare questions: RomanQualityLabelQuestion[];

  declare currentQuestion: RomanQualityLabelQuestion;

  declare options: RomanQualityLabelOptions;

  keys!: {
    majorKey: KeySignature;
    minorKey: KeySignature;
  };

  questionDivId!: string;

  answerDivId!: string;

  constructor(displayDiv: HTMLDivElement, options: RomanQualityLabelOptions) {
    super(displayDiv, options);
    this.targetSpeedFromSkills = RomanQualityLabel.registry.skills;
    // Place custom settings & Layout information here:
    this.settings = {
      instructions: {
        ariaLive: 'alert',
        ariaLiveRegion: [0, 1],
      },
      letters: {
        chordIdButtons: {
          chordTypes: options.chordTypes ? options.chordTypes : 'both',
          root: false,
          inversionType: false,
        },
      },
      checkMark: {},
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: RomanQualityLabelOptions) {
    if (!options.drill && !options.testMode && (!options.questions || !options.questions.length)) {
      throw new Error(
        "Can't find a set of questions on the options object, but NoteDraw is not in drill or test mode."
      );
    }

    return super.init(displayDiv, options);
  }

  generateQuestions(questions = this.options.questions): void {
    const majorKey = new KeySignature(~~(Math.random() * 11) - 4);
    const minorKey = majorKey.getParallelKey();
    this.keys = { majorKey, minorKey };

    this.questions = this.options.drill
      ? this.generateDrillQuestions()
      : this.processManualQuestions(questions!);

    this.questionDivId = `rql${utils.uuid}`;
    this.questionPrompt = `<span id='${this.questionDivId}'>What is the quality of the<br />
      {question} on <span class='accidentals'>{question}</span> in {question}</span>`;

    if (!this.options.testMode) {
      this.questionPrompt += `<br /><small>#{question} of ${this.questions.length}`;
    }

    this.answerDivId = `rql${utils.uuid}`;
    this.questionPrompt += `<br /><span id='${this.answerDivId}'>__</span>`;
  }

  generateDrillQuestions(): RomanQualityLabelQuestion[] {
    const skills = (this.options.skills || []).map(Skills.makeString);
    const qualities = ['Major', 'minor'].filter((quality) =>
      skills.find((s) => s.includes(quality.toLowerCase()))
    );
    const keyQuality = qualities.length === 2 ? 'both' : qualities[0] || 'both';
    const types = ['triad', 'seventh'].filter((type) => skills.find((s) => s.includes(type)));
    const chordTypes = types.length === 2 ? 'both' : `${types[0]}s` || 'both';

    if (this.settings.letters && this.settings.letters.chordIdButtons) {
      this.settings.letters!.chordIdButtons.chordTypes = chordTypes as
        | 'triads'
        | 'sevenths'
        | 'both';
    }

    const qg = new QuestionGenerator();
    return qg
      .generateRomanQuestions(this.options.maxQuestions || 6, {
        keyQuality,
        chordTypes,
        inversions: false,
      })
      .map((q) => ({
        rn: q.rn,
        sd: q.rn.scaleDegree,
        isSeventh: q.rn.isSeventh,
        isMajorKey: q.rn.key.quality === 'Major',
        quality: q.rn.chord.quality,
      }))
      .map((q) => {
        q.rn.setKey(this.keys[q.rn.key.quality === 'Major' ? 'majorKey' : 'minorKey']);
        return q;
      })
      .map(this.questionText)
      .map(this.mapSkills);
  }

  processManualQuestions(questions: string[]): RomanQualityLabelQuestion[] {
    return questions
      .map(this.processQuestion.bind(this))
      .map(this.questionText)
      .map(this.mapSkills);
  }

  processQuestion(
    question: string
  ): Pick<RomanQualityLabelQuestion, 'rn' | 'sd' | 'isSeventh' | 'isMajorKey' | 'quality'> {
    const qParts = question.split('/');
    const numeral = /[i, I, v, V]{1,}/.exec(qParts[0] || '')?.[0];
    let sd = numeral ? romanToInt(numeral) : false;
    if (typeof sd === 'number') {
      sd = `${sd}`;
    } else if (typeof sd === 'boolean') {
      throw new Error(`Couldn't process the question ${question}`);
    }
    console.log(`numeral ${numeral}`);
    console.log('scale degree', sd);
    const isSeventh = question.includes('7');
    const isMajorKey = !question.includes('m');
    const key = this.keys[isMajorKey ? 'majorKey' : 'minorKey'];
    const rn = new RomanNumeral(`${key}: ${qParts[0]}`);
    const sdTableLookup =
      sd === '7' && !isMajorKey ? 'M7' : (sd as keyof typeof validRomans['minor']);
    const triadQualityString = isMajorKey ? 'Major' : 'minor';
    let qualityString: string;
    if (triadQualityString === 'Major') {
      qualityString = validRomans[triadQualityString][
        sdTableLookup as keyof typeof validRomans['Major']
      ][0]![isSeventh ? 1 : 0] as string;
    } else {
      qualityString = validRomans[triadQualityString][
        sdTableLookup as keyof typeof validRomans['minor']
      ]?.[0]![isSeventh ? 1 : 0] as string;
    }
    const quality = Chord.normalizeQuality(qualityString).quality;
    return { rn, sd, isSeventh, isMajorKey, quality };
  }

  // eslint-disable-next-line class-methods-use-this
  questionText(
    q: Omit<RomanQualityLabelQuestion, 'text' | 'questionText' | 'skills'>,
    i: number
  ): Omit<RomanQualityLabelQuestion, 'skills'> {
    const chordType = q.isSeventh ? '7<sup>th</sup> chord' : 'triad';
    const sd = q.sd;
    const keyQuality = q.isMajorKey ? 'Major' : 'minor';
    return { ...q, text: [chordType, sd, keyQuality, `${i + 1}`], questionText: '' };
  }

  // eslint-disable-next-line class-methods-use-this
  mapSkills(question: Omit<RomanQualityLabelQuestion, 'skills'>): RomanQualityLabelQuestion {
    let skill: string;
    if (!question.isSeventh && !question.isMajorKey) {
      skill = 'triad_rn_qualities_minor';
    } else if (!question.isSeventh && question.isMajorKey) {
      skill = 'triad_rn_qualities_major';
    } else if (question.isSeventh && !question.isMajorKey) {
      skill = 'seventh_rn_qualities_minor';
    } else {
      skill = 'seventh_rn_qualities_major';
    }

    return { ...question, skills: [skill] };
  }

  addListeners() {
    // Code to set callbacks & communication to eventually check an answer.
    this.letterPanel!.addCustomButtonCallback((value) => {
      if (value === 'check') this.checkAnswer(false);
      else this.updateAnswerDisplay(value);
    }, this);
  }

  updateAnswerDisplay(value: string | number) {
    this.checkMark!.off();
    const answerDiv =
      // eslint-disable-next-line no-undef
      document.getElementById(this.answerDivId);
    if (value === '__') {
      answerDiv!.removeAttribute('aria-live');
    } else {
      answerDiv!.setAttribute('aria-live', 'assertive');
    }
    answerDiv!.innerHTML = `${value}`;
  }

  newQuestion() {
    super.newQuestion();
    this.currentQuestion.questionText =
      // eslint-disable-next-line no-undef
      document.getElementById(this.questionDivId)!.innerHTML;
    if (this.letterPanel!.chordIdButtons) {
      this.letterPanel!.chordIdButtons.reset();
    }
    this.updateAnswerDisplay('__');
  }

  get answer() {
    return this.letterPanel!.chordIdButtons?.answer;
  }

  playAnswer() {
    if (!this.currentQuestion.rn.isValid) {
      console.error(`invalid rn: ${this.currentQuestion.rn.toString({ key: true })}`);
      return;
    }
    this.player!.stop();
    const nh = this.currentQuestion.rn.chord.getOpenSpacing();
    this.player!.play(nh, { startTime: 0, stopTime: 1.2 });
  }

  checkAnswer(forceSubmit: boolean) {
    if (this.rightAnswerDelay && !forceSubmit) return undefined;
    const answer = this.answer;
    const question = this.currentQuestion;

    if ((!answer || !answer.quality) && !forceSubmit) {
      return this.checkMark!.warn('You must select an answer quality first.');
    }

    // When correct, call rightAnswer();
    // When incorrect, call wrongAnswer();
    const attemptQuality = Chord.normalizeQuality(answer?.quality + (answer?.isSeventh ? '7' : ''));
    attemptQuality.isSeventh = answer?.isSeventh || false;
    const questionQuality = Chord.normalizeQuality(question.quality);
    const qualityMatch =
      attemptQuality.quality.replace('7', '') === questionQuality.quality.replace('7', '');
    const seventhMatch = questionQuality.isSeventh === attemptQuality.isSeventh;
    const correct = qualityMatch && seventhMatch;

    if (this.options.testMode) {
      return this.checkpointAnswer({
        question,
        questionQuality,
        answer,
        correct,
      });
    }

    if (correct) {
      return this.rightAnswer(1000);
    }

    let warning: string;
    if (!seventhMatch && qualityMatch) {
      warning = `This is ${question.isSeventh ? '' : 'not '} a seventh chord.`;
    } else if (!qualityMatch) {
      warning = 'Wrong quality chord.';
    } else {
      warning = '';
    }

    this.wrongAnswerCount++;
    return this.wrongAnswer(this.wrongAnswerCount > 1 ? warning : undefined);
  }

  // ## CheckPoint Mode Questions:
  newCheckpointQuestion(questionString: string) {
    return this.loadingPromise.then(() => {
      this.generateQuestions([questionString]);
      this.newQuestion();
      return Promise.resolve(this.currentQuestion);
    });
  }

  // For checkpoint tests, the answer & data from the current question is
  // logged via a callback.
  getAnswerAndData(data: {
    question: RomanQualityLabelQuestion;
    questionQuality: any;
    answer: any;
    correct: boolean;
  }): AnswerAndData {
    if (!data) {
      return this.checkAnswer(true) as any;
    }
    const { question, questionQuality, answer, correct } = data;
    const answerAndData = {
      question: question.questionText,
      questionType: 'RomanQualityLabel',
      questionText: question.questionText,
      questionSVG: undefined,
      studentAnswerText: answer.quality + (answer.isSeventh ? '7' : ''),
      studentAnswerSVG: undefined,
      correctAnswerText: questionQuality.quality + (questionQuality.isSeventh ? '7' : ''),
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: question.skills,
    };
    return answerAndData;
  }

  static registry = {
    description: 'Label RN Qualities',
    skills: [
      'triad_rn_qualities_major',
      'triad_rn_qualities_minor',
      'seventh_rn_qualities_major',
      'seventh_rn_qualities_minor',
    ],
  };
}

export default RomanQualityLabel;
